import React, { useState } from 'react';

interface FileChange {
  path: string;
  type: 'A' | 'M' | 'D' | string;
}

interface ProgressEvent {
  type?: string;
  status?: string;
  tool?: string;
  stage?: string;
  filePath?: string;
  changes?: FileChange[];
  changed?: { cloud?: string[]; public?: string[]; data?: string[] };
}

const ACTION_META: Record<string, { label: string; color: string }> = {
  A: { label: 'Created', color: '#22c55e' },
  M: { label: 'Modified', color: '#eab308' },
  D: { label: 'Deleted', color: '#ef4444' },
};

function extractChanges(events: ProgressEvent[]): FileChange[] {
  const changes: FileChange[] = [];
  const seen = new Set<string>();

  for (const evt of events) {
    if (evt.changes && Array.isArray(evt.changes)) {
      for (const c of evt.changes) {
        if (c.path && !seen.has(c.path)) {
          seen.add(c.path);
          changes.push(c);
        }
      }
    }
    if (evt.changed && !evt.changes) {
      const allPaths = [
        ...(evt.changed.cloud || []),
        ...(evt.changed.public || []),
        ...(evt.changed.data || []),
      ];
      for (const p of allPaths) {
        if (p && !seen.has(p)) {
          seen.add(p);
          changes.push({ path: p, type: 'M' });
        }
      }
    }
  }
  return changes;
}

function groupByAction(changes: FileChange[]): Record<string, string[]> {
  const groups: Record<string, string[]> = {};
  for (const c of changes) {
    const action = c.type || 'M';
    if (!groups[action]) groups[action] = [];
    groups[action].push(c.path);
  }
  return groups;
}

export default function ExecutionLogCard({ events }: { events: ProgressEvent[] }) {
  const [isExpanded, setIsExpanded] = useState(false);

  const changes = React.useMemo(() => extractChanges(events), [events]);
  const groups = React.useMemo(() => groupByAction(changes), [changes]);
  const actionKeys = Object.keys(groups).sort();

  if (changes.length === 0) return null;

  return (
    <div className="rounded-xl border border-white/[0.06] bg-white/[0.02] overflow-hidden">
      {/* Header */}
      <button
        type="button"
        className="w-full flex items-center gap-2 px-4 py-2.5 hover:bg-white/[0.02] transition-colors"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <svg viewBox="0 0 16 16" fill="none" className="h-3.5 w-3.5 text-white/30 shrink-0">
          <path d="M2 4h12M2 8h8M2 12h10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
        </svg>
        <span className="text-[13px] text-white/50 font-medium">
          {changes.length} {changes.length === 1 ? 'file changed' : 'files changed'}
        </span>
        <div className="flex items-center gap-1.5 ml-auto">
          {actionKeys.map((k) => {
            const meta = ACTION_META[k] || { label: k, color: '#9ca3af' };
            return (
              <span key={k} className="flex items-center gap-1">
                <span className="h-1.5 w-1.5 rounded-full" style={{ backgroundColor: meta.color }} />
                <span className="text-[11px] text-white/30">{groups[k].length}</span>
              </span>
            );
          })}
        </div>
        <svg
          viewBox="0 0 24 24"
          fill="none"
          className="h-3 w-3 text-white/20 shrink-0 transition-transform"
          style={{ transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)' }}
        >
          <path d="M6 9l6 6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>

      {/* Expanded file list */}
      {isExpanded && (
        <div className="px-4 pb-3 pt-1 border-t border-white/[0.04]">
          <div className="space-y-0.5">
            {changes.map((c) => {
              const meta = ACTION_META[c.type] || { label: c.type, color: '#9ca3af' };
              const filename = c.path.split('/').pop() || c.path;
              const dir = c.path.includes('/') ? c.path.substring(0, c.path.lastIndexOf('/') + 1) : '';
              return (
                <div key={c.path} className="flex items-center gap-2 py-0.5">
                  <span
                    className="text-[10px] font-semibold uppercase w-[14px] text-center shrink-0"
                    style={{ color: meta.color }}
                  >
                    {c.type}
                  </span>
                  <span className="text-[12px] font-mono text-white/40 truncate">
                    {dir}<span className="text-white/60">{filename}</span>
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
